import { memo, useState, type FC, type KeyboardEvent } from "react";
import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { CompanyFilters } from "../types";
import type { FilterModule } from "./index";
import { useCompanyFilterSlice, useCompanyFilterActions } from "../hooks";

type JobTitleInclusionMethod = "ANY_PRESENT" | "ALL_PRESENT";

type Slice = {
  include: string[];
  exclude: string[];
  inclusionMethod: JobTitleInclusionMethod;
};

// OppGen's DEFAULT_CONTACT_JOB_TITLE_INCLUDE_EXCLUDE.
const defaultSlice: Slice = {
  include: [],
  exclude: [],
  inclusionMethod: "ANY_PRESENT",
};

const KeywordInput: FC<{
  id: string;
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  chipColor?: "default" | "error";
}> = ({ id, label, values, onChange, chipColor = "default" }) => {
  const [input, setInput] = useState("");

  const add = () => {
    const value = input.trim();
    if (!value) return;
    if (!values.includes(value)) onChange([...values, value]);
    setInput("");
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          id={id}
          fullWidth
          size="small"
          label={label}
          placeholder="Type job title and press Enter or click Add"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e: KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          inputProps={{ "data-testid": `${id}-input` }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={add}
          data-testid={`${id}-add`}
        >
          Add
        </Button>
      </Stack>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
        {values.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            color={chipColor}
            onDelete={() => onChange(values.filter((t) => t !== tag))}
          />
        ))}
      </Box>
    </Box>
  );
};

const ContactJobTitleFilter: FC = memo(() => {
  const slice = useCompanyFilterSlice("contactJobTitle");
  const { setSlice } = useCompanyFilterActions();

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Typography variant="body2" component="div">
        <b>Include</b> contacts with a title containing{" "}
        <Select
          size="small"
          variant="standard"
          value={slice.inclusionMethod}
          onChange={(e) =>
            setSlice("contactJobTitle", {
              ...slice,
              inclusionMethod: e.target.value as JobTitleInclusionMethod,
            })
          }
          data-testid="contacts-job-title-inclusion-method"
        >
          <MenuItem value="ALL_PRESENT">all</MenuItem>
          <MenuItem value="ANY_PRESENT">any</MenuItem>
        </Select>{" "}
        of the following keywords
      </Typography>
      <KeywordInput
        id="contacts-job-title-include"
        label="Included Job Titles"
        values={slice.include}
        onChange={(include) =>
          setSlice("contactJobTitle", { ...slice, include })
        }
      />
      <Typography variant="body2">Exclude the following job titles</Typography>
      <KeywordInput
        id="contacts-job-title-exclude"
        label="Excluded Job Titles"
        values={slice.exclude}
        onChange={(exclude) =>
          setSlice("contactJobTitle", { ...slice, exclude })
        }
        chipColor="error"
      />
    </Stack>
  );
});

ContactJobTitleFilter.displayName = "ContactJobTitleFilter";

export const contactJobTitleModule: FilterModule<Slice> = {
  key: "contactJobTitle",
  defaultSlice,
  Component: ContactJobTitleFilter,
  toCompanyFilters: (slice): Partial<CompanyFilters> =>
    slice.include.length > 0 || slice.exclude.length > 0
      ? {
          contactFilters: {
            jobTitleKeywords: {
              include: slice.include,
              exclude: slice.exclude,
              inclusionMethod: slice.inclusionMethod,
            },
          },
        }
      : {},
  fromCompanyFilters: (filters): Slice => ({
    include: filters.contactFilters?.jobTitleKeywords?.include ?? [],
    exclude: filters.contactFilters?.jobTitleKeywords?.exclude ?? [],
    inclusionMethod:
      filters.contactFilters?.jobTitleKeywords?.inclusionMethod ??
      defaultSlice.inclusionMethod,
  }),
  getChips: (slice, setSlice) => [
    ...slice.include.map((t) => ({
      id: `contacts-job-title-include-${t}`,
      label: `Job Title: ${t}`,
      filterKey: "contactJobTitle" as const,
      onRemove: () =>
        setSlice({ ...slice, include: slice.include.filter((x) => x !== t) }),
    })),
    ...slice.exclude.map((t) => ({
      id: `contacts-job-title-exclude-${t}`,
      label: `Excl. Job Title: ${t}`,
      filterKey: "contactJobTitle" as const,
      onRemove: () =>
        setSlice({ ...slice, exclude: slice.exclude.filter((x) => x !== t) }),
    })),
  ],
  getCount: (slice) => slice.include.length + slice.exclude.length,
};
